import { postMessageToMobileApp } from 'components/app/helpers';
import { POST_MESSAGE_TO_MOBILE_EVENTS } from 'constants/enums';
import {
  HYPERVERGE_KYC_RESULT_STATUS,
  MOBILE_APP_KYC_RESULT_MESSAGE_NAME,
} from 'constants/hyperverge';
import { MIXPANEL_EVENT_NAMES } from 'constants/mixpanelEventNames';
import { FEATURE_FLAG_HYPERVERGE_SDK_SUPPORTED } from 'constants/rnApp';
import { trackMixpanelEvent } from 'helpers/mixpanel-init';
import { RN_MESSAGE_EVENT_TYPE } from 'hooks/useRNMessageEvents';
import {
  HypervergeKYCResult,
  LaunchHypervergeKYCWidgetArgs,
  PossibleHypervergeKYCResultStatus,
} from 'types/hyperverge';

const getFinalStatusFromHypervergeResult = (result: HypervergeKYCResult | null) => {
  if (result?.status === HYPERVERGE_KYC_RESULT_STATUS.ERROR) {
    trackMixpanelEvent(MIXPANEL_EVENT_NAMES.HYPERVERGE_KYC_ERROR, {
      error_message: result.errorMessage,
      error_code: result.errorCode,
      latest_module: result.latestModule,
    });
  }

  if (result?.status === HYPERVERGE_KYC_RESULT_STATUS.USER_CANCELLED) {
    trackMixpanelEvent(MIXPANEL_EVENT_NAMES.HYPVERGE_USER_CANCELLED, {
      latest_module: result.latestModule,
    });
  }

  return {
    finalStatus: result?.status || HYPERVERGE_KYC_RESULT_STATUS.ERROR,
    errorCode: result?.errorCode,
    latestModule: result?.latestModule,
  };
};

/**
 * @see [Hyperverge KYC Docs](https://documentation.hyperverge.co/sdks/)
 */
const launchHypervergeKYCWidget = (
  args: LaunchHypervergeKYCWidgetArgs,
  customInputs?: Record<string, unknown>
) => {
  return new Promise<{
    finalStatus: PossibleHypervergeKYCResultStatus;
    errorCode?: string;
    latestModule?: string;
  }>((resolve, reject) => {
    /**
     * All these fields are part of `/v2/profile/vendor_link` api except transactionId which is userId.
     */
    const { workflowId, accessToken, transactionId, uniqueId } = args;

    /**
     * If we are calling this method in mobile app then we need to trigger native sdk, so passing message to mobile app.
     * Also we want the result from the mobile app sdk, so setting up a listener to receive the message event from mobile app.
     */
    if (FEATURE_FLAG_HYPERVERGE_SDK_SUPPORTED) {
      /**
       * Message event handler.
       */
      const messageHandler = (event: MessageEvent) => {
        // If message is not from mobile-app, ignore.
        if (event?.data?.type !== RN_MESSAGE_EVENT_TYPE) {
          return;
        }

        const { name, data } = event?.data?.payload ?? {};

        /**
         * If any other message is received then ignore that message.
         */
        if (name !== MOBILE_APP_KYC_RESULT_MESSAGE_NAME) {
          return;
        }

        // When we get null from mobile app, it means there was some error.
        if (data === null) {
          reject();
        } else {
          const finalStatus = getFinalStatusFromHypervergeResult(data);
          resolve(finalStatus);
        }

        /**
         * Removing event listener here as cleanup.
         */
        window.removeEventListener('message', messageHandler);
      };

      /**
       * Attach event listener for mobile app sdk results.
       */
      window.addEventListener('message', messageHandler);

      /**
       * Initiate mobile app sdk by passing a message containing relevant fields.
       */

      postMessageToMobileApp(POST_MESSAGE_TO_MOBILE_EVENTS.LAUNCH_HYPERVERGE_SDK_KYC, {
        workflowId,
        accessToken,
        transactionId,
        customInputs,
        uniqueId,
      });

      return;
    }

    /**
     * In case of web, first check if script has loaded and function is available.
     */
    if (typeof window.HyperKycConfig === 'function') {
      /**
       * Initiate Hyperverge Config.
       */
      const hyperKycConfig = new window.HyperKycConfig(
        accessToken,
        workflowId,
        transactionId
      );

      if (uniqueId) {
        hyperKycConfig.setUniqueId(uniqueId);
      }

      hyperKycConfig.setInputs(customInputs);

      const handler = (HyperKycResult: HypervergeKYCResult) => {
        const finalStatus = getFinalStatusFromHypervergeResult(HyperKycResult);
        resolve(finalStatus);
      };

      window.HyperKYCModule.launch(hyperKycConfig, handler);
    } else {
      /**
       * Reject promise to indicate there was an error.
       * In this case script has not loaded or not available for some unknown reasons.
       */

      trackMixpanelEvent(MIXPANEL_EVENT_NAMES.HYPERVERGE_SDK_LOAD_FAIL);

      reject();
    }
  });
};

export { launchHypervergeKYCWidget };
